import { Crown, Sparkles, Sun } from "lucide-react";
import { getPlanTier, TIER_THEME, tierBadgeLabel } from "@/lib/planTier";

/**
 * Tier-aware portal greeting hero.
 * - Sprint: gold gradient banner, animated shimmer, crown icon
 * - Studio: ember-accented compact welcome with small crown
 * - Starter/none: clean welcome
 */
export default function PortalGreeting({ client }) {
    const name = client?.name?.split(" ")[0] || client?.name || "there";
    const tier = getPlanTier(client?.plan);
    const theme = TIER_THEME[tier];
    const label = tierBadgeLabel(tier);

    if (tier === "sprint" || tier === "studio") {
        const isSprint = tier === "sprint";
        return (
            <section
                data-testid={`portal-greeting-${tier}`}
                className={`relative overflow-hidden border border-gold/40 bg-gradient-to-br from-gold/[0.08] via-transparent to-gold/[0.04] ring-1 ring-gold/30 mb-10 px-6 py-7 lg:px-10 lg:py-9`}
            >
                <div className="absolute inset-0 pointer-events-none gold-shimmer" aria-hidden />
                <div className="relative flex items-start justify-between gap-6 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-gold mb-3">
                            <Crown className="w-3.5 h-3.5" />
                            {label}
                        </div>
                        <h1
                            data-testid="portal-greeting-heading"
                            className="font-display tracking-tightest text-bone text-4xl lg:text-5xl leading-[1.05]"
                        >
                            Welcome back,{" "}
                            <span className="font-serif-i italic text-gold">{name}.</span>
                        </h1>
                        <p className="mt-3 text-bone-300 text-sm lg:text-base max-w-xl">
                            {isSprint
                                ? "Your Sprint engagement gives you the full creative weight of the studio. We're moving on your projects right now."
                                : "Your Studio engagement is in full motion. Below is where things stand today."}
                        </p>
                    </div>
                    {isSprint && (
                        <div className="hidden sm:flex items-center gap-2 px-4 py-2 border border-gold/30 bg-gold/5 text-gold text-[10px] uppercase tracking-[0.25em]">
                            <Sparkles className="w-3 h-3" />
                            Reserved · 5 / quarter
                        </div>
                    )}
                </div>
            </section>
        );
    }

    return (
        <section
            data-testid="portal-greeting-starter"
            className="mb-10"
        >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-bone-500 mb-3">
                <Sun className="w-3.5 h-3.5" />
                {label || "Your portal"}
            </div>
            <h1
                data-testid="portal-greeting-heading"
                className="font-display tracking-tightest text-bone text-4xl lg:text-5xl leading-[1.05]"
            >
                Welcome back,{" "}
                <span className="font-serif-i italic text-ember">{name}.</span>
            </h1>
            <p className="mt-3 text-bone-300 text-sm lg:text-base max-w-xl">
                Here&apos;s what&apos;s happening with your projects today.
            </p>
        </section>
    );
}
